import { FC, useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { FeedbackSubIssueEnum, type SubmitFeedbackIO } from '@frontend/api-call-intel';
import { useTranslation } from '@frontend/i18n';
import { HOTSPOT_TARGET, OnboardingInfoTooltip } from '@frontend/product-onboarding';
import { theme } from '@frontend/theme';
import {
  ChecklistMenuField,
  Modal,
  ModalControlModalProps,
  Text,
  TextareaField,
  useForm,
} from '@frontend/design-system';
import { FeedbackFeatureSubIssueMap, FeedbackFeatureType } from '../../constants';
import {
  useCallIntelFeedbackFeatureLabels,
  useCallIntelFeedbackSubIssueLabels,
} from '../../hooks/use-call-intel-defaults';
import FeedbackIssueInput from '../input/feedback-issue.input';

type FeedbackModalV2Props = {
  modalProps: ModalControlModalProps;
  onSubmit: (values: {
    featureFeedbackGroupList: SubmitFeedbackIO['input']['featureFeedbacks'];
    feedbackFeatures: FeedbackFeatureType[];
    subIssues: FeedbackSubIssueEnum[];
    feedback: string;
    isPositive: boolean;
  }) => void;
  isLoading?: boolean;
  trackingIdBase?: string;
  isPositiveFeedback?: boolean;
  availableFeatures?: FeedbackFeatureType[];
};

export const FeedbackModalV2: FC<FeedbackModalV2Props> = ({
  modalProps,
  onSubmit,
  isLoading,
  isPositiveFeedback,
  availableFeatures = [],
  trackingIdBase,
}) => {
  const { t } = useTranslation('call-intelligence');

  const feedbackFeatureLabelMap = useCallIntelFeedbackFeatureLabels();
  const feedbackSubIssueLabelMap = useCallIntelFeedbackSubIssueLabels();

  const { getFieldProps, values, reset, isComplete } = useForm({
    fields: {
      feedback: { type: 'text', value: '' },
      feedbackFeatures: {
        type: 'checklist',
        required: !isPositiveFeedback,
        value: [],
        minRequired: isPositiveFeedback ? 0 : 1,
      },
      subIssues: { type: 'checklist', value: [] },
    },
  });

  const feedbackField = getFieldProps('feedback');
  const feedbackFeaturesField = getFieldProps('feedbackFeatures');
  const subIssuesField = getFieldProps('subIssues');

  const availableSubIssuesByGroup = useMemo(() => {
    const groups: Record<string, string[]> = {};
    values.feedbackFeatures?.forEach((issue) => {
      const subs = FeedbackFeatureSubIssueMap[issue as FeedbackFeatureType];
      if (subs && subs.length > 0) {
        groups[issue] = subs;
      }
    });
    return groups;
  }, [values.feedbackFeatures]);

  const hasSubIssues = Object.keys(availableSubIssuesByGroup).length > 0;

  const handleSubmit = () => {
    onSubmit({
      feedbackFeatures: values.feedbackFeatures as FeedbackFeatureType[],
      subIssues: values.subIssues as FeedbackSubIssueEnum[],
      feedback: values.feedback as string,
      isPositive: !!isPositiveFeedback,
      featureFeedbackGroupList: values.feedbackFeatures?.map((feature) => ({
        feature: feature,
        subIssues: availableSubIssuesByGroup[feature]?.filter((subIssue) =>
          (values.subIssues as string[]).includes(subIssue)
        ),
      })) as SubmitFeedbackIO['input']['featureFeedbacks'],
    });
  };

  useEffect(() => {
    if (modalProps.show) {
      reset();
    }
  }, [modalProps.show]);

  return (
    <Modal
      {...modalProps}
      css={styles.modal}
      disableCloseOnEscape={isLoading}
      disableCloseOnOverlayClick={isLoading}
      minWidth={600}
    >
      <OnboardingInfoTooltip
        target={HOTSPOT_TARGET.CALL_INSIGHT_PANEL_NEGATIVE_FEEDBACK_MODAL}
        placement='top'
        offset={24}
        enabled={isPositiveFeedback === false}
      >
        <Modal.Header onClose={modalProps.onClose} closeBtnTrackingId={`${trackingIdBase}:feedback-modal-close`}>
          {isPositiveFeedback ? t('Share Positive Feedback') : t('Report an Issue')}
        </Modal.Header>
        <Modal.Body>
          <Text css={styles.subtitle}>
            {isPositiveFeedback
              ? t('Please share details about what you found valuable in the Call Insights.')
              : t('Please share details to help us improve your Call Insights.')}
          </Text>

          {!isPositiveFeedback && (
            <div css={styles.negativeFields}>
              <ChecklistMenuField
                {...feedbackFeaturesField}
                data-testid='feedback-feature-select'
                label={t('Select Feature')}
                placeholder={t('Select One')}
                showSearchBar={false}
                showSelectionBar={false}
                customRender={({ selectedKeys }) => {
                  const selectedLabels: string[] = selectedKeys.map(
                    (key) => feedbackFeatureLabelMap[key as FeedbackFeatureType]
                  );

                  return (
                    <FeedbackIssueInput
                      selectedLabels={selectedLabels}
                      selectedKeys={selectedKeys}
                      onRemoveLabel={(id) =>
                        feedbackFeaturesField.onChange?.({ name: 'feedbackFeatures', value: [id] })
                      }
                    />
                  );
                }}
                floatingStyleOverrides={{ maxWidth: '100%', padding: theme.spacing(2, 0) }}
              >
                {availableFeatures.map((option) => (
                  <ChecklistMenuField.Option key={option} value={option}>
                    {feedbackFeatureLabelMap[option]}
                  </ChecklistMenuField.Option>
                ))}
              </ChecklistMenuField>

              {hasSubIssues && (
                <ChecklistMenuField
                  {...subIssuesField}
                  data-testid='feedback-sub-issue-select'
                  label={t('Select Issues (Optional)')}
                  placeholder={t('Select Issues (Optional)')}
                  showSearchBar={true}
                  showSelectionBar={false}
                  customRender={({ selectedKeys }) => {
                    const selectedLabels: string[] = selectedKeys.map(
                      (key) => feedbackSubIssueLabelMap[key as FeedbackSubIssueEnum] ?? key
                    );
                    return (
                      <FeedbackIssueInput
                        selectedLabels={selectedLabels}
                        selectedKeys={selectedKeys}
                        onRemoveLabel={(id) => subIssuesField.onChange?.({ name: 'subIssues', value: [id] })}
                      />
                    );
                  }}
                  floatingStyleOverrides={{ maxWidth: '100%' }}
                >
                  {Object.entries(availableSubIssuesByGroup).map(([group, issues]) =>
                    feedbackFeaturesField.value?.length > 1 ? (
                      <ChecklistMenuField.Group
                        key={group}
                        label={feedbackFeatureLabelMap[group as FeedbackFeatureType]}
                      >
                        {issues.map((issue) => (
                          <ChecklistMenuField.Option key={issue} value={issue}>
                            {feedbackSubIssueLabelMap[issue as FeedbackSubIssueEnum] ?? issue}
                          </ChecklistMenuField.Option>
                        ))}
                      </ChecklistMenuField.Group>
                    ) : (
                      issues.map((issue) => (
                        <ChecklistMenuField.Option key={issue} value={issue}>
                          {feedbackSubIssueLabelMap[issue as FeedbackSubIssueEnum] ?? issue}
                        </ChecklistMenuField.Option>
                      ))
                    )
                  )}
                </ChecklistMenuField>
              )}
            </div>
          )}

          <TextareaField
            {...feedbackField}
            label={t('Feedback (Optional)')}
            data-trackingid={`${trackingIdBase}:feedback-modal-feedback-input`}
          />
        </Modal.Body>

        <Modal.Footer
          primary={{
            disabled: !isComplete,
            label: t('Submit'),
            onClick: handleSubmit,
            trackingId: `${trackingIdBase}:feedback-modal-submit-action`,
          }}
        />
      </OnboardingInfoTooltip>
    </Modal>
  );
};

const styles = {
  modal: css`
    width: 100%;
  `,
  subtitle: css`
    margin-bottom: ${theme.spacing(1)};
  `,
  negativeFields: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    margin-bottom: ${theme.spacing(2)};
  `,
};
